import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Design System
import { Message } from 'jl-design-system/components/message/Message';

// Types
import { AppDispatch } from 'types/RootState.types';
import { StreamlinedPaymentsProps } from './StreamlinedPayments.types';

// Config
import streamlinedPaymentsState from './StreamlinedPayments.state';
import scrollConstants from '@constants/scrollConstants';
import errorCodeConstants from '@constants/errorCodeConstants';
import { useWindowSize } from '@context/window/WindowSizeContext';
import { setClearpayEnabledStatus } from '@redux/actions/payment/clearpayPaymentActions';
import { setKlarnaEnabledStatus } from '@redux/actions/payment/klarnaPaymentActions';
import {
  getSubmitOrderErrorMessageBody,
  getSubmitOrderErrorMessageTitle,
} from '@utils/payment/getSubmitOrderErrorMessage';

// Components
import PayLaterCategory from './pay-later-category';
import PayNowCategory from './pay-now-category/PayNowCategory';
import POSCreditOptions from './pos-credit-options/';
import ClearpayConnectMessage from '@components/clearpay-connect-message';
import Container from '@components/container';
import KlarnaConnectMessage from '@components/klarna-connect-message';
import MarketingOptIn from '@components/marketing-opt-in';
import PaymentButton from '@components/payment-button';
import PaymentCardDeletedMessages from '@components/payment-card-deleted-messages/PaymentCardDeletedMessages';
import PaymentCardsDueToExpireMessages from '@components/payment-cards-due-to-expire-messages';
import PayPalConnectMessage from '@components/paypal-connect-message';
import PaymentMessage from '@components/payment-message';
import POSTermsConditions from '@components/pos-terms-conditions';

const StreamlinedPayments = ({
  disabled = false,
  isZeroOutstandingBalance = false,
  resetAfterError,
}: StreamlinedPaymentsProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    creditTypeGroups = [],
    isApplePayAvailable,
    isGooglePayAvailable,
    isPayPalAvailable,
    outOfStockItems = [],
    partnerDiscountEnabled,
    paymentCardDeleteApiCallFailed,
    paymentCardDeleted,
    paymentPageAPICallActive,
    paymentWallet = [],
    payPalPayLaterEligible,
    savedPaymentCards = [],
    selectedPaymentType,
    showCallCentreNumberOn3DSError,
    showClearpay,
    showKlarna,
    showMarketingPreferencesPrompt,
    showPartnerCardError,
    showPaymentMessage,
    submitOrderError,
  } = useSelector(streamlinedPaymentsState);
  const prevShowClearpay = useRef<boolean>(showClearpay?.isEligible);
  const prevShowKlarna = useRef<boolean>(showKlarna?.isEligible);

  const showErrorMessage = submitOrderError && outOfStockItems.length === 0 && !paymentPageAPICallActive;

  const { desktopResolution, sidebarIsVisible } = useWindowSize();

  // TODO I can't remember why we do these clearpay things here but we may need to do the same for klarna
  // or remove these if no longer needed
  useEffect(() => {
    dispatch(setClearpayEnabledStatus(showClearpay.isEligible));
    dispatch(setKlarnaEnabledStatus(showKlarna.isEligible));
  }, []);

  useEffect(() => {
    if (prevShowClearpay?.current !== showClearpay.isEligible) {
      dispatch(setClearpayEnabledStatus(showClearpay.isEligible));
      prevShowClearpay.current = showClearpay.isEligible;
    }
    if (prevShowKlarna?.current !== showKlarna.isEligible) {
      dispatch(setKlarnaEnabledStatus(showKlarna.isEligible));
      prevShowKlarna.current = showKlarna.isEligible;
    }
  }, [showClearpay, showKlarna]);

  const addNewCardProps = {
    desktopResolution,
    disabled,
    isZeroOutstandingBalance,
    resetAfterError,
    savedPaymentCards,
    selectedPaymentType,
    showOrderTotals: !sidebarIsVisible,
  };

  return (
    <>
      <Container background="white" data-scroll={scrollConstants.STREAMLINED_PAYMENTS_CONTAINER}>
        {showPaymentMessage && <PaymentMessage />}

        <Container paddingBottom="3" paddingTop={isZeroOutstandingBalance ? '0' : '2'} paddingX="3">
          {showErrorMessage && (
            <Container marginBottom="3">
              <Message
                body={getSubmitOrderErrorMessageBody({
                  submitOrderError,
                  showPartnerCardError,
                  showCallCentreNumberOn3DSError,
                })}
                dataId={submitOrderError.code ?? errorCodeConstants.UI_SCROLL_TO_ERROR}
                dataTestId="submitOrderError"
                scrollToMessageOnMount
                title={getSubmitOrderErrorMessageTitle(submitOrderError, showPartnerCardError)}
                type="error"
              />
            </Container>
          )}

          <PayPalConnectMessage />
          <ClearpayConnectMessage />
          <KlarnaConnectMessage />

          <PaymentCardsDueToExpireMessages paymentCards={paymentWallet} />

          <PaymentCardDeletedMessages
            paymentCardDeleteApiCallFailed={paymentCardDeleteApiCallFailed}
            paymentCardDeleted={paymentCardDeleted}
          />

          {isZeroOutstandingBalance && <PaymentButton disabled={disabled} ignoreAvailablePaymentProviders />}

          {!isZeroOutstandingBalance && (
            <>
              <PayNowCategory
                addNewCardProps={addNewCardProps}
                desktopResolution={desktopResolution}
                disabled={disabled}
                isApplePayAvailable={isApplePayAvailable}
                isGooglePayAvailable={isGooglePayAvailable}
                isPayPalAvailable={isPayPalAvailable}
                partnerDiscountEnabled={partnerDiscountEnabled}
                resetAfterError={resetAfterError}
                showOrderTotals={!sidebarIsVisible}
              />

              <Container testId="posc-options">
                <POSCreditOptions
                  creditTypeGroups={creditTypeGroups}
                  disabled={disabled}
                  showOrderTotals={!sidebarIsVisible}
                />
              </Container>

              <PayLaterCategory
                clearpayEligible={showClearpay}
                desktopResolution={desktopResolution}
                disabled={disabled}
                klarnaEligible={showKlarna}
                partnerDiscountEnabled={partnerDiscountEnabled}
                payPalPayLaterEligible={payPalPayLaterEligible}
                selectedPaymentType={selectedPaymentType}
                showOrderTotals={!sidebarIsVisible}
              />
            </>
          )}

          {showMarketingPreferencesPrompt && <MarketingOptIn />}
        </Container>
      </Container>

      {creditTypeGroups?.length > 0 && <POSTermsConditions />}
    </>
  );
};

export default StreamlinedPayments;
